<template>
  <div
    class="draggable-box"
    :style="boxStyle"
    @touchstart="handleTouchStart"
    @touchmove="handleTouchMove"
    @touchend="handleTouchEnd"
    @click="handleClick"
  >
    <slot></slot>
  </div>
</template>

<script>
export default {
  name: 'DraggableBox',
  props: {
    // 是否启用拖动
    draggable: {
      type: Boolean,
      default: true
    },
    // 距离右下角的偏移量
    offset: {
      type: Object,
      default: () => ({ x: 20, y: 20 })
    }
  },
  data() {
    return {
      position: { x: 0, y: 0 },
      startPos: { x: 0, y: 0 },
      isDragging: false,
      boxSize: { w: 0, h: 0 },
      screenSize: { w: window.innerWidth, h: window.innerHeight }
    }
  },
  computed: {
    boxStyle() {
      return {
        transform: `translate(${this.position.x}px, ${this.position.y}px)`,
        position: 'fixed',
        left: '0',
        top: '0',
        'z-index': 998
      }
    }
  },
  mounted() {
    this.initBoxSize()
    this.initScreenSize()
    this.initPosition() // 初始化位置到右下角
    window.addEventListener('resize', this.handleResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    // 初始化元素尺寸
    initBoxSize() {
      const el = this.$el
      this.boxSize = {
        w: el.offsetWidth,
        h: el.offsetHeight
      }
    },
    // 初始化屏幕尺寸
    initScreenSize() {
      this.screenSize = {
        w: window.innerWidth,
        h: window.innerHeight
      }
    },
    // 初始化位置到右下角
    initPosition() {
      this.$nextTick(() => {
        this.position = {
          x: this.screenSize.w - this.boxSize.w - this.offset.x,
          y: this.screenSize.h - this.boxSize.h - this.offset.y
        }
        this.ensurePositionInBoundary()
      })
    },
    // 窗口大小变化处理
    handleResize() {
      this.initScreenSize()
      this.initBoxSize()
      // 保持相对右下角的位置
      this.position = {
        x: this.screenSize.w - this.boxSize.w - this.offset.x,
        y: this.screenSize.h - this.boxSize.h - this.offset.y
      }
      this.ensurePositionInBoundary()
    },
    // 确保元素在边界内
    ensurePositionInBoundary() {
      this.position.x = Math.max(
        0,
        Math.min(
          this.position.x,
          this.screenSize.w - this.boxSize.w
        )
      )
      this.position.y = Math.max(
        0,
        Math.min(
          this.position.y,
          this.screenSize.h - this.boxSize.h
        )
      )
    },
    // 触摸开始
    handleTouchStart(e) {
      if (!this.draggable) return
      
      this.isDragging = false
      const touch = e.touches[0]
      this.startPos = {
        x: touch.clientX - this.position.x,
        y: touch.clientY - this.position.y
      }
    },
    // 触摸移动
    handleTouchMove(e) {
      if (!this.draggable) return
      
      this.isDragging = true
      const touch = e.touches[0]
      
      let newX = touch.clientX - this.startPos.x
      let newY = touch.clientY - this.startPos.y
      
      // 限制边界
      newX = Math.max(0, Math.min(newX, this.screenSize.w - this.boxSize.w - 10))
      newY = Math.max(10, Math.min(newY, this.screenSize.h - this.boxSize.h - this.getIOSBottomSafeArea()))
      
      this.position = { x: newX, y: newY }
      e.preventDefault()
    },
    // 触摸结束
    handleTouchEnd() {
      if (!this.draggable) return
      
      if (this.isDragging) {
        setTimeout(() => {
          this.isDragging = false
        }, 100)
      } else {
        this.isDragging = false
      }
    },
    // 点击处理
    handleClick() {
      if (!this.isDragging) {
        this.$emit('click')
      }
    },
    // 重置到右下角
    resetToCorner() {
      this.position = {
        x: this.screenSize.w - this.boxSize.w - this.offset.x,
        y: this.screenSize.h - this.boxSize.h - this.offset.y
      }
    },
    getIOSBottomSafeArea() {
      // 计算视窗高度和文档高度的差异
      const viewportHeight = window.innerHeight;
      const documentHeight = document.documentElement.clientHeight;
      
      // 差异通常就是底部安全区域高度
      const safeArea = viewportHeight - documentHeight;
      
      return safeArea > 0 ? safeArea : 0;
    }
  }
}
</script>

<style scoped>
.draggable-box {
  touch-action: none;
  user-select: none;
  cursor: pointer;
  will-change: transform; /* 优化动画性能 */
}
</style>